// client/src/writer/components/OptimizedBlueprintButton.tsx
// PHASE 2: Blueprint Button Three-State System Implementation

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Edit, RefreshCw, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ModelSelectionDialog } from './ModelSelectionDialog';

interface OptimizedBlueprintButtonProps {
  blueprintExists: boolean;
  isLoading: boolean;
  creditBalance: number;
  requiredCredits: number;
  outlineIdea: string;
  onGenerateInitial: () => void;
  onRegenerate: () => void;
  onGenerateWithModel?: (modelId: string) => void;
  className?: string;
  lastError?: string;
  failedModel?: string;
  userPlan?: string;
}

export const OptimizedBlueprintButton: React.FC<OptimizedBlueprintButtonProps> = ({
  blueprintExists,
  isLoading,
  creditBalance,
  requiredCredits,
  outlineIdea,
  onGenerateInitial,
  onRegenerate,
  onGenerateWithModel,
  className = '',
  lastError,
  failedModel,
  userPlan = 'FREE'
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  
  const hasInsufficientCredits = creditBalance < requiredCredits;
  const isDisabled = isLoading || !outlineIdea.trim() || hasInsufficientCredits;

  const handleModelSelect = (model: any) => {
    if (onGenerateWithModel) {
      onGenerateWithModel(model.api_name);
    }
  };

  // STATE 1: No Blueprint - Show Generate Button
  if (!blueprintExists) {
    return (
      <>
        <div className={cn("space-y-4", className)}>
          {/* Error Message Display */}
          {lastError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-800">Generation Failed</p>
                  <p className="text-sm text-red-600">{lastError}</p>
                  {failedModel && (
                    <p className="text-xs text-red-500">Failed model: {failedModel}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={onGenerateInitial}
              disabled={isDisabled}
              className="flex-1 py-3 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Generating Initial Blueprint...
                </>
              ) : (
                'Generate Initial Blueprint (Details & World)'
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowModelSelector(true)}
              disabled={isLoading}
              className="px-3"
              title="Choose AI Model"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="text-center space-y-2">
            <p className="text-xs text-gray-500">
              This will generate suggestions for Book Details and World Building.
              You can then refine these and generate chapter outlines in the "Chapters Building" tab.
            </p>
            
            {hasInsufficientCredits && (
              <p className="text-sm text-red-500">
                You need {requiredCredits} credits to generate the initial blueprint. 
                Current balance: {creditBalance}.
              </p>
            )}
          </div>
        </div>

        <ModelSelectionDialog
          open={showModelSelector}
          onOpenChange={setShowModelSelector}
          taskType="blueprint"
          onModelSelect={handleModelSelect}
          baseCost={requiredCredits}
          failedModel={failedModel}
          errorMessage={lastError}
          userPlan={userPlan}
          creditBalance={creditBalance}
        />
      </>
    );
  }

  // STATE 2: Blueprint Exists - Show View/Edit Mode
  return (
    <>
      <div className={cn("space-y-4", className)}>
        {/* Blueprint exists - show completion status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">Blueprint Generated</p>
              <p className="text-xs text-green-600">
                Your story blueprint is ready. You can now access other tabs or regenerate with updated details.
              </p>
            </div>
          </div>
        </div>

        {/* Edit Mode Controls */}
        {isEditMode ? (
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-orange-800">
                    Regenerate Blueprint
                  </p>
                  <p className="text-sm text-orange-600">
                    This will use <strong>{requiredCredits} credits</strong> to regenerate the blueprint with your updated details.
                    Your current blueprint will be replaced.
                  </p>
                  <p className="text-xs text-orange-500">
                    Credit balance: {creditBalance}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  onRegenerate();
                  setIsEditMode(false);
                }}
                disabled={hasInsufficientCredits || isLoading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  `Confirm Regeneration (${requiredCredits} credits)`
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setIsEditMode(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          /* View Mode Controls */
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEditMode(true)}
              disabled={isLoading}
              className="flex-1"
            >
              <Edit className="w-4 h-4 mr-2" />
              Regenerate Blueprint
            </Button>
          </div>
        )}
      </div>

      <ModelSelectionDialog
        open={showModelSelector}
        onOpenChange={setShowModelSelector}
        taskType="blueprint"
        onModelSelect={handleModelSelect}
        baseCost={requiredCredits}
        failedModel={failedModel}
        errorMessage={lastError}
        userPlan={userPlan}
        creditBalance={creditBalance}
      />
    </>
  );

  // STATE 2: Blueprint Exists - View Mode (Default)
  if (!isEditMode) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-center mb-3">
            <div className="inline-flex items-center text-green-700">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span className="font-semibold">Blueprint Generated</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              Your story blueprint is ready. Navigate to other tabs to view and edit details.
            </p>
          </div>
          
          <Button
            onClick={() => setIsEditMode(true)}
            variant="outline"
            className="w-full py-2 border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Blueprint Settings
          </Button>
        </div>
      </div>
    );
  }

  // STATE 3: Edit/Regeneration Mode - Show Warning and Options
  return (
    <div className={cn("space-y-4", className)}>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="text-center mb-4">
          <div className="inline-flex items-center text-yellow-800 mb-2">
            <AlertTriangle className="w-5 h-5 mr-2" />
            <h3 className="font-semibold">Regenerate Blueprint</h3>
          </div>
          <p className="text-sm text-yellow-700">
            This will overwrite your existing blueprint data. Your current book details 
            and world-building will be replaced with new AI-generated content based on your updated idea.
          </p>
        </div>
        
        <div className="space-y-2">
          <Button
            onClick={() => {
              onRegenerate();
              setIsEditMode(false); // Exit edit mode after regeneration starts
            }}
            disabled={isDisabled}
            className="w-full py-2 bg-red-600 hover:bg-red-700 text-white"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Regenerating Blueprint...
              </>
            ) : (
              'Regenerate Blueprint (Overwrite Existing)'
            )}
          </Button>
          
          <Button
            onClick={() => setIsEditMode(false)}
            variant="outline"
            className="w-full py-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          
          {hasInsufficientCredits && (
            <p className="text-sm text-red-500 text-center mt-2">
              You need {requiredCredits} credits to regenerate the blueprint. 
              Current balance: {creditBalance}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OptimizedBlueprintButton;